import type { GlobalStore } from '../../../state/zustand';
import type {
  ScenarioLocation,
  ScenarioOrder,
} from '../../../lib/planningData';
import type { LocationId, PortCallData, PortCallEntry } from '../../core/Core';
import {
  getIntegrationPortCosts,
  type PortCostByVcMaps,
} from './portCostIntegration';
import {
  getDefaultPortCostByType,
  type PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';
import {
  getLatestHistoricalPortCost,
  type LatestHistoricalPortCostEstimates,
} from './portCostLatestHistory';
import type { PortCostByCoaMaps } from '../../hooks/usePortCostCoa';
import type { DefaultMap } from '@seaber/common/lib/collections';
import type { ClientId } from '../../core/ClientId';
import type { Currency, ScenarioTimeline_ShipFragment } from '../../../graphql';
import { PortType } from '../../../graphql';
import type { VoyageGroup } from '../../core/Timelines';
import type { TimeData } from '../../core/TimeData';
import {
  isDischargeEventTimeData,
  isLoadEventTimeData,
  isPortCallArrivalEventTimeData,
  isSingleEventTimeData,
  isTCOrderEventTimeData,
  SingleEventType,
  type Events,
} from '../../core/TimeData';
import type { DefaultPortCostEstimates } from './portCostDefault';
import {
  getMaxDefaultPortCostForLocation,
  getPortCostDefaults,
} from './portCostDefault';
import type { HistoricalPortCostEstimates } from './portCostHistory';
import {
  getHistoricalPortCostEstimates,
  mapExternalPortCostEstimatesToParams,
} from './portCostHistory';
import type { CostParam, CostsByPortCall } from './costs';
import { type PortCostPredictionByPortCall } from './portCostPrediction';
import { type MarcuraPortCosts } from '../../marcura/context';

type ShipmentsByPortCallId = DefaultMap<ClientId | null, Set<ClientId | null>>;

/**
 * Determine the port type (L/D/M/F) from events in a port call. Returns undefined if no port type is found.
 *
 * Note: Currently we distinguish between loading, discharging, M and bunkering port calls.
 * Other port types are not supported yet.
 */
export function getPortTypeFromPortCall(
  tds: TimeData<Events>[],
): PortType | undefined {
  const hasLoadEvent = tds.some(isLoadEventTimeData);
  const hasDischargeEvent = tds.some(isDischargeEventTimeData);

  if (hasLoadEvent && hasDischargeEvent) {
    return PortType.M;
  }
  if (hasLoadEvent) {
    return PortType.L;
  }
  if (hasDischargeEvent) {
    return PortType.D;
  }

  // Check if this is a bunkering port call (has a bunkering single event)
  const hasBunkeringEvent = tds.some(
    (td) =>
      isSingleEventTimeData(td) && td.event.event === SingleEventType.BUNKERING,
  );
  if (hasBunkeringEvent) {
    return PortType.F;
  }

  return undefined;
}

export function getPortCosts({
  currencyRates,
  baseCurrency,
  locationById,
  portCalls,
  portCostByVcMaps,
  portCostEstimateMaps,
  portCostByCoaMaps,
  portCostMarcuraMaps,
  portCostPredictions,
  shipmentsByPortCallId,
  vessel,
}: {
  currencyRates: GlobalStore['currencyRates'];
  baseCurrency: Currency;
  locationById: Map<LocationId, ScenarioLocation>;
  portCalls: PortCallData[];
  portCostByVcMaps: PortCostByVcMaps;
  portCostEstimateMaps: PortCostEstimateMaps;
  portCostByCoaMaps: PortCostByCoaMaps;
  portCostMarcuraMaps: MarcuraPortCosts;
  portCostPredictions: PortCostPredictionByPortCall | null;
  shipmentsByPortCallId: ShipmentsByPortCallId;
  vessel: ScenarioTimeline_ShipFragment;
  voyageGroups: VoyageGroup<Events>[];
}): CostsByPortCall {
  const costs: CostsByPortCall = new Map();
  const vesselName = vessel.name;
  if (!vesselName) return costs;

  for (const pc of portCalls) {
    const arrivalEventTd = pc.tds.find(isPortCallArrivalEventTimeData);
    const tcTimeData = pc.tds.filter(isTCOrderEventTimeData);
    const orders = tcTimeData.map((entry) => entry.event.order);
    const locationId = pc.locationId;
    // NOTE: Don't know why this location map is used here, but keeping it for now to not break anything
    const location = locationById.get(locationId);
    const locationName = location?.name;
    const portCallEntry = arrivalEventTd?.event.entry;

    if (!location || !locationName || !portCallEntry || !arrivalEventTd) {
      continue;
    }

    // Port costs are ignored for canal transit port calls
    if (arrivalEventTd.event.isCanalTransitPortCall) {
      continue;
    }

    const portType = getPortTypeFromPortCall(pc.tds);

    const sources = getPortCallCostSources(
      portCallEntry,
      orders,
      pc.id,
      locationId,
      locationName,
      vessel,
      portCostEstimateMaps,
      portCostByVcMaps,
      portCostByCoaMaps,
      portCostMarcuraMaps,
      portCostPredictions,
      baseCurrency,
      portType,
    );

    const numShipments = shipmentsByPortCallId.get(pc.id).size || 1;

    const params = mapExternalPortCostEstimatesToParams(
      sources.historicalValue,
      currencyRates,
    );

    costs.set(pc.id, [
      {
        type: 'PORT_CALL',
        name: 'Port cost',
        value:
          (sources.coaPortCost?.value ?? sources.portCostEstimate ?? 0) /
          numShipments,
        numShipments,
        params,
        locations: location ? [location] : [],
        options: {
          ...sources,
          portCallEntry,
        },
      },
    ]);
  }

  return costs;
}

export type PortCallCostSources = {
  coaPortCost: { value: number; currency: string } | undefined;
  defaultValue: DefaultPortCostEstimates | undefined;
  historicalValue: HistoricalPortCostEstimates | undefined;
  latestHistoricalValue: LatestHistoricalPortCostEstimates | undefined;
  integrationCost:
    | { value: number; currency: string; source?: 'PortLog' }
    | undefined;
  oneTimeOverrideCost: number | null;
  portCostDefaultId: number | null;
  portCostEstimate: number | undefined;
  predictionCost: { value: number; params: CostParam[] } | undefined;
  selectedId: number | undefined;
  useHistoricalPortCost: boolean;
  useLatestHistoricalPortCost: boolean;
  useIntegrationPortCost: boolean;
  useOneTimeOverrideCost: boolean;
  locationMaxDefault: { cost: number; currency: string } | undefined;
};

/** Returns all potential port cost source values for a given port call */
export function getPortCallCostSources(
  portCallEventOrEntry: Pick<
    PortCallEntry,
    | 'oneTimeOverrideCost'
    | 'portCostDefaultId'
    | 'useHistoricalPortCost'
    | 'useLatestHistoricalPortCost'
    | 'useIntegrationPortCost'
    | 'useOneTimeOverrideCost'
  >,
  orders: ScenarioOrder[],
  portCallId: ClientId | null,
  portCallLocationId: LocationId,
  portCallLocationName: string,
  vessel: ScenarioTimeline_ShipFragment,
  portCostEstimateMaps: PortCostEstimateMaps,
  portCostByVcMaps: PortCostByVcMaps,
  portCostByCoaMaps: PortCostByCoaMaps | null,
  portCostMarcuraMaps: MarcuraPortCosts,
  portCostPredictions: PortCostPredictionByPortCall | null,
  baseCurrency: Currency,
  portType: PortType | undefined,
): PortCallCostSources {
  const {
    byName,
    byPrimaryAttributeId,
    latestByPrimaryAttributeId,
    defaultById,
    defaultByTypeId,
    defaultByVesselId,
    locationIdsByName,
  } = portCostEstimateMaps;

  const defaultValue = getPortCostDefaults(
    portCallLocationId,
    portCallEventOrEntry.portCostDefaultId,
    portCostEstimateMaps,
    vessel,
  );

  const {
    oneTimeOverrideCost,
    useHistoricalPortCost,
    useLatestHistoricalPortCost,
    useOneTimeOverrideCost,
    useIntegrationPortCost,
    portCostDefaultId,
  } = portCallEventOrEntry;

  const vesselId = vessel.id;
  const vesselWeight = vessel.weightCapacity;
  const primaryVesselAttributeId = vessel.primaryAttributeId;

  let selectedId: number | undefined;
  let portCostEstimate: number | undefined;
  let coaPortCost: { value: number; currency: string } | undefined;

  if (portCostByCoaMaps) {
    for (const order of orders) {
      if (!order.coaId) continue;
      const portCostsByVesselId = portCostByCoaMaps
        .get(order.coaId)
        .get(vessel.id)
        .get(portCallLocationId);
      const portCostsByVesselClassId = portCostByCoaMaps
        .get(order.coaId)
        .get(primaryVesselAttributeId ? -primaryVesselAttributeId : null)
        .get(portCallLocationId);
      const portCost = portCostsByVesselId[0] || portCostsByVesselClassId[0];

      if (portCost) {
        coaPortCost = portCost
          ? {
              value: +portCost.cost,
              currency: portCost.coaContractByCoaContractId?.currency ?? '',
            }
          : undefined;
        break;
      }
    }
  }

  if (oneTimeOverrideCost && useOneTimeOverrideCost) {
    portCostEstimate = oneTimeOverrideCost;
  }

  if (!portCostEstimate && portCostDefaultId) {
    portCostEstimate = defaultById.get(portCostDefaultId);
    selectedId = portCostDefaultId;
  }

  const integrationCost = getIntegrationPortCosts(
    portCallLocationId,
    vesselWeight,
    orders,
    portCostByVcMaps,
    portCostMarcuraMaps,
    baseCurrency,
  );

  if (useIntegrationPortCost && integrationCost) {
    portCostEstimate = integrationCost.value;
  }

  if (!portCostEstimate && primaryVesselAttributeId && !useHistoricalPortCost) {
    const defByTypeIdCost = getDefaultPortCostByType(
      defaultByTypeId,
      portCallLocationId,
      primaryVesselAttributeId,
      locationIdsByName,
    );

    if (defByTypeIdCost) {
      selectedId = defByTypeIdCost.id;
      portCostEstimate = defByTypeIdCost.cost;
    }
  }

  const defByNameCost = defaultByVesselId.get(portCallLocationId, vesselId);
  if (!portCostEstimate && defByNameCost) {
    selectedId = defByNameCost.id;
    portCostEstimate = defByNameCost.cost;
  }

  if (!portCostEstimate) {
    const typeIds = vessel?.vesselAttributesAssociationsByShipId.nodes ?? [];
    for (const type of typeIds) {
      const defByTypeIdCost =
        type?.vesselAttributeId &&
        defaultByTypeId.get(portCallLocationId, type.vesselAttributeId);
      if (defByTypeIdCost) {
        selectedId = type.id;
        portCostEstimate = defByTypeIdCost.cost;
        break;
      }
    }
  }

  let historicalValue: HistoricalPortCostEstimates | undefined = undefined;

  if (vessel.name && portType) {
    historicalValue = getHistoricalPortCostEstimates({
      locationName: portCallLocationName,
      vesselName: vessel.name,
      byName,
      primaryVesselAttributeId,
      byPrimaryAttributeId,
      portType,
    });
  }

  if ((!portCostEstimate || useHistoricalPortCost) && portCallLocationName) {
    portCostEstimate = historicalValue?.cost;
    selectedId = undefined;
  }

  const latestHistoricalValue = portType
    ? getLatestHistoricalPortCost(
        portCallLocationName,
        primaryVesselAttributeId,
        latestByPrimaryAttributeId,
        portType,
      )
    : undefined;

  const portCostPrediction = portCostPredictions?.get(portCallId);
  const predictionCost = portCostPrediction
    ? {
        value: portCostPrediction.value,
        params: portCostPrediction.params,
      }
    : undefined;

  const locationMaxDefault = getMaxDefaultPortCostForLocation(
    portCallLocationId,
    portCostEstimateMaps,
  );

  return {
    coaPortCost,
    defaultValue,
    historicalValue,
    latestHistoricalValue,
    integrationCost,
    oneTimeOverrideCost,
    portCostDefaultId,
    portCostEstimate,
    predictionCost,
    selectedId,
    useHistoricalPortCost,
    useLatestHistoricalPortCost,
    useIntegrationPortCost,
    useOneTimeOverrideCost,
    locationMaxDefault,
  };
}
